import { useCallback, useMemo } from 'react';

import { ValidationRuleId } from '@/lib/graphql/generated/graphql';
import { DisplayMode } from '@/types/display-mode';
import type { ColumnFilter, ColumnFiltersState } from '@tanstack/react-table';
import { atom, useAtom } from 'jotai';

export enum ColumnFilterId {
  listPrice = 'listPrice',
  discountedPrice = 'discountedPrice',
  choosePrice = 'choosePrice',
  chooseDiscount = 'chooseDiscount',
  stockQuantity = 'stockQuantity',
  validationErrors = 'validationErrors',
  vatRate = 'vatRate',
  __displayMode = '__displayMode',
  __searchTerms = '__searchTerms',
  __comparisonFilter = '__comparisonFilter',
}

export const createDefaultColumnFilters = (): ColumnFiltersState => [
  {
    id: ColumnFilterId.listPrice,
    value: [0, Number.POSITIVE_INFINITY],
  },
  {
    id: ColumnFilterId.discountedPrice,
    value: [0, Number.POSITIVE_INFINITY],
  },
  {
    id: ColumnFilterId.choosePrice,
    value: [0, Number.POSITIVE_INFINITY],
  },
  {
    id: ColumnFilterId.chooseDiscount,
    // Lower bound is unbounded (−∞) so negative discounts are not filtered out
    // by default. A negative chooseDiscount is a valid value to read (e.g. a
    // markup), and a [0, +∞] default would silently hide every such item.
    value: [Number.NEGATIVE_INFINITY, Number.POSITIVE_INFINITY],
  },
  {
    id: ColumnFilterId.stockQuantity,
    value: [0, Number.POSITIVE_INFINITY],
  },
  {
    id: ColumnFilterId.vatRate,
    value: [] as string[],
  },
  {
    id: ColumnFilterId.validationErrors,
    value: [] as ValidationRuleId[],
  },
  {
    id: ColumnFilterId.__displayMode,
    value: DisplayMode.SELECTED,
  },
  {
    id: ColumnFilterId.__searchTerms,
    value: '',
  },
  {
    id: ColumnFilterId.__comparisonFilter,
    value: [] as string[],
  },
];

export const columnFiltersState = atom<ColumnFiltersState>(
  createDefaultColumnFilters()
);

/** Selected validation rule IDs for filtering (empty = no filter on error type). */
export const validationErrorsState = atom<ValidationRuleId[]>([]);

/** All validation rule IDs, for building the filter UI (checkboxes, etc.). */
export const ALL_VALIDATION_RULE_IDS: ValidationRuleId[] =
  Object.values(ValidationRuleId);

export function useSetColumnFilter() {
  const [columnFilters, setColumnFilters] = useAtom(columnFiltersState);

  const getColumnFilter = useCallback(
    (columnId: ColumnFilterId) =>
      columnFilters.find((filter) => filter.id === columnId) as ColumnFilter,
    [columnFilters]
  );

  const setColumnFilter = useCallback(
    (columnId: ColumnFilterId, value: unknown) => {
      setColumnFilters((previous) =>
        previous.map((filter) => {
          if (filter.id !== columnId) return filter;
          return {
            ...filter,
            value,
          };
        })
      );
    },
    [setColumnFilters]
  );

  const resetAndSetColumnFilter = useCallback(
    (columnId: ColumnFilterId, value: unknown) => {
      setColumnFilters((previous) => {
        const displayMode = previous.find(
          (filter) => filter.id === ColumnFilterId.__displayMode
        );
        return createDefaultColumnFilters().map((filter) => {
          if (filter.id === columnId) return { ...filter, value };
          if (filter.id === ColumnFilterId.__displayMode && displayMode) {
            return displayMode;
          }
          return filter;
        });
      });
    },
    [setColumnFilters]
  );

  return useMemo(
    () => ({
      getColumnFilter,
      setColumnFilter,
      resetAndSetColumnFilter,
    }),
    [getColumnFilter, setColumnFilter, resetAndSetColumnFilter]
  );
}
